import React, { useMemo, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ConsequenceReveal from '../shared/ConsequenceReveal';
import styles from './BrokenLessonEngine.module.css';

const ZONES = {
  A: { id: 'A', x: 60, y: 40, width: 200, height: 30, label: 'Multiple browser tabs' },
  B: { id: 'B', x: 340, y: 100, width: 120, height: 80, label: 'Sticky note with password' },
  C: { id: 'C', x: 60, y: 90, width: 260, height: 180, label: 'School website' },
  D: { id: 'D', x: 330, y: 200, width: 130, height: 70, label: 'Brightness slider' }
};

const BrokenLessonEngine = ({ gamifiedQuestion, onAnswer, accent = '#6a4c93' }) => {
  const [typedDone, setTypedDone] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showOutcome, setShowOutcome] = useState(false);
  const [sceneReady, setSceneReady] = useState(false);
  const [hoveredZone, setHoveredZone] = useState(null);
  const [zoomZone, setZoomZone] = useState(null);

  const options = useMemo(() => gamifiedQuestion?.options?.slice(0, 4) || [], [gamifiedQuestion?.options]);
  const selectedOption = options.find((option) => option.label === selected);
  const sceneType = String(gamifiedQuestion?.sceneType || '').toLowerCase();
  const gameType = String(gamifiedQuestion?.gameType || '').toUpperCase();
  const isSecurity = sceneType.includes('privacy') || sceneType.includes('security') || sceneType.includes('password');
  const isClickable = gameType === 'CHAOS_CLICK' || gameType === 'HOTSPOT_IMAGE';

  useEffect(() => {
    const timer = setTimeout(() => setSceneReady(true), 800);
    return () => clearTimeout(timer);
  }, []);

  const handleSelect = (label) => {
    if (!sceneReady || selected) return;
    setSelected(label);

    // Find zone for zoom effect
    const zoneIndex = ['A', 'B', 'C', 'D'].indexOf(label);
    const zoneId = Object.keys(ZONES)[zoneIndex];
    if (zoneId && isSecurity) {
      setZoomZone(ZONES[zoneId]);
    }

    setTimeout(() => setShowOutcome(true), 800);
  };

  const getZoneForLabel = (label) => {
    const index = options.findIndex(opt => opt.label === label);
    return Object.values(ZONES)[index];
  };

  return (
    <div className={styles.engine} style={{ '--accent': accent }}>
      {/* SCENE AREA - Top */}
      <div className={styles.sceneArea}>
        <motion.div
          className={styles.monitor}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Browser chrome */}
          <div className={styles.chrome}>
            <span className={styles.dot} />
            <span className={styles.dot} />
            <span className={styles.dot} />
            <div className={styles.address}>
              <span className={styles.url}>school.edu/student-portal</span>
            </div>
          </div>

          {/* Screen content */}
          <div className={styles.screen}>
            <svg viewBox="0 0 480 300" className={styles.screenSvg}>
              {/* Background */}
              <rect x="0" y="0" width="480" height="300" fill="#fff" />

              {/* Zone A: Browser tabs */}
              <motion.g
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className={styles.clickableZone}
                onClick={() => isClickable && handleSelect('A')}
                onMouseEnter={() => setHoveredZone('A')}
                onMouseLeave={() => setHoveredZone(null)}
              >
                <rect x="10" y="10" width="90" height="25" rx="4" className={styles.tab} />
                <rect x="105" y="10" width="90" height="25" rx="4" className={`${styles.tab} ${hoveredZone === 'A' ? styles.tabHighlight : ''}`} />
                <rect x="200" y="10" width="90" height="25" rx="4" className={styles.tab} />
                <rect x="295" y="10" width="90" height="25" rx="4" className={styles.tab} />
                <rect x="390" y="10" width="40" height="25" rx="4" className={styles.tabNew} />
              </motion.g>

              {/* Zone B: Sticky note with password */}
              <motion.g
                initial={{ opacity: 0, scale: 0.8, rotate: -5 }}
                animate={{ opacity: 1, scale: 1, rotate: -3 }}
                transition={{ delay: 0.5, type: 'spring' }}
                className={styles.clickableZone}
                onClick={() => isClickable && handleSelect('B')}
                onMouseEnter={() => setHoveredZone('B')}
                onMouseLeave={() => setHoveredZone(null)}
              >
                <motion.rect
                  x="340"
                  y="50"
                  width="120"
                  height="80"
                  rx="4"
                  className={`${styles.stickyNote} ${hoveredZone === 'B' ? styles.stickyHighlight : ''}`}
                  animate={hoveredZone === 'B' ? { rotate: [-3, 0, -3] } : { rotate: -3 }}
                  transition={{ duration: 0.3 }}
                />
                <text x="400" y="85" textAnchor="middle" className={styles.stickyText}>
                  pass123
                </text>
                <text x="400" y="105" textAnchor="middle" className={styles.stickyLabel}>
                  password
                </text>

                {/* Warning glow on B */}
                {isSecurity && hoveredZone !== 'B' && (
                  <motion.circle
                    cx="400"
                    cy="90"
                    r="50"
                    className={styles.warningGlow}
                    animate={{ opacity: [0.2, 0.5, 0.2] }}
                    transition={{ repeat: Infinity, duration: 2 }}
                  />
                )}
              </motion.g>

              {/* Zone C: School website content */}
              <motion.g
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className={styles.clickableZone}
                onClick={() => isClickable && handleSelect('C')}
                onMouseEnter={() => setHoveredZone('C')}
                onMouseLeave={() => setHoveredZone(null)}
              >
                <rect x="20" y="50" width="300" height="220" rx="6" className={`${styles.contentArea} ${hoveredZone === 'C' ? styles.contentHighlight : ''}`} />
                {/* School logo placeholder */}
                <circle cx="80" cy="90" r="30" className={styles.logoPlaceholder} />
                <text x="80" y="95" textAnchor="middle" className={styles.logoText}>S</text>
                {/* Content lines */}
                <rect x="130" y="80" width="160" height="8" rx="2" className={styles.contentLine} />
                <rect x="130" y="100" width="120" height="8" rx="2" className={styles.contentLine} />
                <rect x="40" y="140" width="260" height="6" rx="2" className={styles.contentLine} />
                <rect x="40" y="160" width="260" height="6" rx="2" className={styles.contentLine} />
                <rect x="40" y="180" width="200" height="6" rx="2" className={styles.contentLine} />
              </motion.g>

              {/* Zone D: Brightness slider panel */}
              <motion.g
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 }}
                className={styles.clickableZone}
                onClick={() => isClickable && handleSelect('D')}
                onMouseEnter={() => setHoveredZone('D')}
                onMouseLeave={() => setHoveredZone(null)}
              >
                <rect x="340" y="150" width="120" height="120" rx="6" className={`${styles.panel} ${hoveredZone === 'D' ? styles.panelHighlight : ''}`} />
                <text x="400" y="175" textAnchor="middle" className={styles.panelTitle}>Display</text>
                {/* Slider */}
                <rect x="355" y="195" width="90" height="6" rx="3" className={styles.sliderTrack} />
                <circle cx="380" cy="198" r="8" className={styles.sliderThumb} />
                {/* Brightness icon */}
                <text x="400" y="240" textAnchor="middle" className={styles.brightnessIcon}>☀</text>
              </motion.g>

              {/* Zone B zoom animation on click */}
              <AnimatePresence>
                {zoomZone && selected === 'B' && (
                  <motion.g
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={{ opacity: 1, scale: 1.2 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                  >
                    <rect x="200" y="110" width="80" height="80" rx="4" className={styles.zoomedNote} />
                    <text x="240" y="155" textAnchor="middle" className={styles.zoomedText}>🔒</text>
                  </motion.g>
                )}
              </AnimatePresence>
            </svg>
          </div>
        </motion.div>
      </div>

      {/* QUESTION TEXT */}
      <div className={styles.questionText}>
        {gamifiedQuestion?.situation}
      </div>

      {/* ANSWER OPTIONS - Bottom */}
      <div className={styles.optionsArea}>
        {options.map((option, index) => {
          const zone = Object.values(ZONES)[index];
          const isHovered = hoveredZone === ['A', 'B', 'C', 'D'][index];
          const isSelected = selected === option.label;

          return (
            <motion.button
              key={option.label}
              type="button"
              className={`${styles.choiceBtn} ${isSelected ? styles.selected : ''} ${selected && !isSelected ? styles.dimmed : ''}`}
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.8 + index * 0.1, type: 'spring', stiffness: 100 }}
              whileHover={!selected ? { y: -2, borderColor: accent } : {}}
              whileTap={!selected ? { scale: 0.98 } : {}}
              onClick={() => handleSelect(option.label)}
              disabled={!!selected}
            >
              <span className={styles.choiceLabel}>{option.label}</span>
              <span className={styles.choiceZone}>{zone?.label}</span>
              <span className={styles.choiceText}>{option.actionText || option.text}</span>
            </motion.button>
          );
        })}
      </div>

      {/* Narration */}
      <p className={styles.narration}>{gamifiedQuestion?.narration}</p>

      <ConsequenceReveal
        visible={showOutcome}
        tone={selected === 'B' ? 'success' : 'warning'}
        message={selectedOption?.consequence || 'Digital decision captured.'}
        duration={1600}
        onDone={() => {
          setShowOutcome(false);
          if (selected) onAnswer(selected);
        }}
      />
    </div>
  );
};

export default BrokenLessonEngine;
